import i18n from 'i18next';

import { StripeVolumePricingConfig } from '../../../../../shared/graphql/graphql';
import { Tier } from '../../../../../shared/types/tiers';

const formatTierValue = (num: number): string => {
  if (num >= 1000) {
    return num / 1000 + i18n.t('thousand-prefix');
  }

  return num.toString();
};

export const getTierRangeString = (min: number, max?: number | null): string =>
  max
    ? `${formatTierValue(min)} - ${formatTierValue(max)}`
    : `${formatTierValue(min)}+`;

/**
 * Adjust volume pricing tiers setting top and bottom limits of orderable area.
 * @param volumePricing tiers array from pricing config
 * @param minAreaAmount minimum area amount available to order
 * @param maxAreaAmount max area amount available to order
 * @returns adjusted tiers array
 */
export const applyMinMaxAreaAmount = (
  volumePricing: StripeVolumePricingConfig[],
  minAreaAmount: number,
  maxAreaAmount: number,
): StripeVolumePricingConfig[] =>
  volumePricing.map((tier) => ({
    ...tier,
    min: Math.max(tier?.min ?? 0, minAreaAmount),
    price: (tier.min ?? 0) >= maxAreaAmount ? null : tier.price,
  }));

export const calculateTiersFromArea = (
  areaAmount: number,
  volumePricing: StripeVolumePricingConfig[],
): Tier[] => {
  let remainingArea = areaAmount;
  const tiers: Tier[] = [];

  if (areaAmount < (volumePricing[0]?.min ?? 0)) {
    return [];
  }

  volumePricing.forEach((tier) => {
    const tierMin = tier.min ?? 0;
    const tierMax = tier.max ?? Number.NaN;

    if (remainingArea <= 0) {
      return;
    }

    const tierArea = Math.min(remainingArea, tierMax - tierMin);
    const tierCost = tierArea * (tier.price ?? 0);
    remainingArea -= tierArea;

    tiers.push({
      max: tierMax,
      min: tierMin,
      amount: tierArea,
      unitPrice: tier.price ?? 0,
      totalPrice: tierCost,
    });
  });

  return tiers;
};
